import {
  LoadBasketRead,
  MaterialQuantityUnit,
} from "src/store/api/generatedApi";
import { sorted } from "helpers";
import { BasketLayer, GroupedMaterial, MaterialKey } from "./types";

export const ADJUSTMENT_AMOUNT = 1;

export const createMaterialKey = (
  basketId: number,
  materialName: string,
  layerNumber: number
): MaterialKey => `${basketId}:${materialName}:${layerNumber}`;

/* A basket's mass/grab layers ordered bottom→top; ratio layers are excluded. */
export const getBasketLayerStack = (basket: LoadBasketRead): BasketLayer[] =>
  sorted(
    basket.materials.filter((layer) => layer.quantity.unit !== "ratio"),
    (layer) => layer.layer_number
  ).map((layer) => ({
    materialName: layer.material_name,
    materialId: layer.material_id,
    layerNumber: layer.layer_number,
    targetMass: layer.quantity.value,
    unit: layer.quantity.unit,
  }));

/* One row per (material, layer_number); a split layer becomes two rows. */
export const getGroupedMaterials = (
  baskets: LoadBasketRead[]
): GroupedMaterial[] => {
  const seenPairs = new Map<
    string,
    { materialName: string; layerNumber: number }
  >();
  for (const basket of baskets) {
    for (const layer of basket.materials) {
      const pairKey = `${layer.material_name}:${layer.layer_number}`;
      if (!seenPairs.has(pairKey)) {
        seenPairs.set(pairKey, {
          materialName: layer.material_name,
          layerNumber: layer.layer_number,
        });
      }
    }
  }

  return Array.from(seenPairs.values()).map(({ materialName, layerNumber }) => {
    const masses = baskets.map((basket) => {
      const layer = basket.materials.find(
        (l) =>
          l.material_name === materialName && l.layer_number === layerNumber
      );
      return layer ? layer.quantity.value : 0;
    });

    const layerInfo = baskets.reduce<
      Record<number, { materialId: number; unit: MaterialQuantityUnit }>
    >((acc, basket) => {
      const layer = basket.materials.find(
        (l) =>
          l.material_name === materialName && l.layer_number === layerNumber
      );
      if (layer) {
        acc[basket.load_basket_id] = {
          materialId: layer.material_id,
          unit: layer.quantity.unit,
        };
      }
      return acc;
    }, {});

    return {
      materialName,
      layerNumber,
      masses,
      layerInfo,
    };
  });
};

export const formatMassDifference = (
  targetMass: number,
  filledMass: number
): { target: string; current: string } => ({
  target: `${Math.round(targetMass)}`,
  current: `${Math.round(filledMass)}`,
});
